import React from 'react';
import { Card, CardHeader, CardBody, Button } from '@nextui-org/react';
import { Link } from 'react-router-dom';
import { FiCheckCircle, FiBook, FiPlayCircle, FiTarget, FiUsers, FiCalendar, FiFileText, FiSettings, FiVideo, FiZap, FiCompass } from 'react-icons/fi';
import HeaderComponent from '../components/General/HeaderComponent';

const steps = [
  {
    icon: <FiTarget />,
    title: 'Proje Oluşturun',
    desc: 'Dashboard\'dan "Aktif Projeler" bölümüne tıklayarak yeni bir proje oluşturun. Projenize isim verin ve başlangıç ayarlarını yapın.',
    step: '1'
  },
  {
    icon: <FiFileText />,
    title: 'Görevler Ekleyin',
    desc: 'Projenize görevler ekleyerek işlerinizi organize edin. Görevlere öncelik seviyesi atayabilir ve detaylı açıklamalar yazabilirsiniz.',
    step: '2'
  },
  {
    icon: <FiCalendar />,
    title: 'Takviminizi Yönetin',
    desc: 'Etkinliklerinizi takvime ekleyin ve yaklaşan etkinliklerinizi takip edin. Tarihleri belirleyerek planlamalarınızı yapın.',
    step: '3'
  },
  {
    icon: <FiUsers />,
    title: 'Takım Oluşturun',
    desc: 'Organizasyon oluşturarak ekibinizi davet edin. Görevleri takım üyelerine atayın ve işbirliği yapın.',
    step: '4'
  },
  {
    icon: <FiBook />,
    title: 'Notlar Alın',
    desc: 'Notlar bölümünden önemli bilgileri kaydedin. İnceleme bekleyen notlarınızı yönetin ve düzenli tutun.',
    step: '5'
  },
  {
    icon: <FiSettings />,
    title: 'Ayarlarınızı Yapın',
    desc: 'Profil sayfanızdan kişisel bilgilerinizi güncelleyin. Temanızı değiştirin ve tercihlerinizi ayarlayın.',
    step: '6'
  },
];

const features = [
  {
    icon: <FiTarget />,
    title: 'Proje Yönetimi',
    desc: 'Projelerinizi kolayca oluşturun, düzenleyin ve takip edin. Proje detaylarına girerek görevler, zaman çizelgesi ve ekibinizi yönetin.'
  },
  {
    icon: <FiCalendar />,
    title: 'Takvim Entegrasyonu',
    desc: 'Etkinliklerinizi takvime ekleyin, yaklaşan etkinlikleri görüntüleyin ve zamanlamalarınızı optimize edin.'
  },
  {
    icon: <FiUsers />,
    title: 'Takım İşbirliği',
    desc: 'Organizasyonlar oluşturarak ekibinizle çalışın. Görev atamaları yapın ve ilerlemeyi birlikte takip edin.'
  },
  {
    icon: <FiFileText />,
    title: 'Not ve Görev Yönetimi',
    desc: 'Notlar ve görevler oluşturun, öncelik seviyeleri atayın ve işlerinizi organize edin.'
  }
];

const tips = [
  'Dashboard\'da tüm önemli bilgilerinizi tek bakışta görebilirsiniz.',
  'Proje detay sayfasından görevler, zaman çizelgesi ve ekibinizi yönetebilirsiniz.',
  'Notlar bölümünden önemli bilgileri kaydedip düzenli tutabilirsiniz.',
  'Premium\'a yükselterek daha fazla özelliğin kilidini açabilirsiniz.',
];

function NasilKullanirim() {
  return (
    <div className="w-full dark:bg-dark-100 bg-stone-100 transition duration-300 flex flex-col lg:mt-0 mt-20">
      <div className="container mx-auto px-4 py-8">
        <HeaderComponent title={'Nasıl Kullanırım?'} />

        {/* Hero Section */}
        <div className="relative overflow-hidden rounded-xl border border-gray-200 dark:border-dark-300 bg-gradient-to-br from-stone-200 via-stone-100 to-white dark:from-dark-200 dark:via-dark-200 dark:to-dark-300 p-[1px] mb-8">
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-white/20 dark:bg-white/10 blur-3xl rounded-full" />
          <div className="rounded-[11px] bg-white dark:bg-dark-300 px-5 md:px-8 py-8 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-2xl md:text-4xl font-semibold text-[#283252] dark:text-darkWhite-300 mb-4">
                HeyPena'ya Hoş Geldiniz!
              </h1>
              <p className="text-base md:text-lg opacity-80 dark:text-darkWhite-200 mb-6">
                Projelerinizi sistematik bir şekilde yönetmek için adım adım rehberimizi takip edin. 
                HeyPena ile tüm işlerinizi organize edin, takımınızla işbirliği yapın ve verimliliğinizi artırın.
              </p>
              <div className="inline-flex items-center gap-2 text-xs md:text-sm px-4 py-2 rounded-full bg-amber-50 dark:bg-dark-200 text-amber-700 dark:text-darkWhite-300">
                <FiPlayCircle className="text-lg" />
                <span>Başlamak için aşağıdaki adımları takip edin</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mb-8 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { to: '/projects/create', label: 'Proje Oluştur', icon: <FiTarget /> },
            { to: '/calendar', label: 'Takvim', icon: <FiCalendar /> },
            { to: '/notes', label: 'Notlar', icon: <FiFileText /> },
            { to: '/organizations', label: 'Organizasyonlar', icon: <FiUsers /> },
          ].map((x, i) => (
            <Link key={i} to={x.to} className="group">
              <div className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 px-4 py-3 transition hover:border-orange-300">
                <div className="text-[#283252] dark:text-darkWhite-300 group-hover:text-orange-600 group-hover:scale-110 transition">{x.icon}</div>
                <div className="text-sm font-medium text-[#283252] dark:text-darkWhite-300">{x.label}</div>
              </div>
            </Link>
          ))}
        </div>

        {/* Steps Section */}
        <div className="mb-10">
          <h2 className="text-xl md:text-2xl font-semibold text-[#283252] dark:text-darkWhite-300 mb-6 text-center">
            Başlangıç Adımları
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {steps.map((step, idx) => (
              <Card key={idx} className="border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 shadow-none">
                <CardHeader className="flex items-start gap-3 pb-2">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-stone-100 dark:bg-dark-200 text-[#283252] dark:text-darkWhite-300 text-lg font-bold">
                    {step.step}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="text-amber-600">{step.icon}</div>
                      <span className="text-base font-semibold text-[#283252] dark:text-darkWhite-300">{step.title}</span>
                    </div>
                    <p className="text-sm opacity-80 dark:text-darkWhite-200 mt-2">{step.desc}</p>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>

        {/* Features Section */}
        <div className="mb-10">
          <h2 className="text-xl md:text-2xl font-semibold text-[#283252] dark:text-darkWhite-300 mb-6 text-center">
            Temel Özellikler
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {features.map((feature, idx) => (
              <Card key={idx} className="border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 shadow-none">
                <CardHeader className="flex items-start gap-3">
                  <div className="mt-[2px] text-[#283252] dark:text-darkWhite-300 text-xl">{feature.icon}</div>
                  <div>
                    <div className="text-base font-semibold text-[#283252] dark:text-darkWhite-300 mb-1">{feature.title}</div>
                    <div className="text-sm opacity-80 dark:text-darkWhite-200">{feature.desc}</div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>

        {/* Video / Demo Placeholder */}
        <div className="mb-10">
          <h2 className="text-xl md:text-2xl font-semibold text-[#283252] dark:text-darkWhite-300 mb-4 text-center">
            2 Dakikada HeyPena
          </h2>
          <div className="relative overflow-hidden rounded-xl border border-dashed border-gray-300 dark:border-dark-300 bg-white dark:bg-dark-300 p-0 shadow-none">
            <div className="aspect-video w-full flex items-center justify-center">
              <div className="flex flex-col items-center justify-center text-amber-600 opacity-80">
                <FiVideo className="text-5xl mb-2" />
                <div className="text-sm">Yakında: Kısa tanıtım videosu</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="mb-10">
          <h2 className="text-xl md:text-2xl font-semibold text-[#283252] dark:text-darkWhite-300 mb-6 text-center">
            İpuçları
          </h2>
          <Card className="border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 shadow-none">
            <CardBody>
              <ul className="space-y-3">
                {tips.map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm dark:text-darkWhite-200">
                    <div className="mt-1">
                      <FiCheckCircle className="text-emerald-600" />
                    </div>
                    <span className="opacity-80">{tip}</span>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        </div>

        {/* Onboarding Checklist & Shortcuts */}
        <div className="mb-10 grid md:grid-cols-2 gap-4">
          <Card className="border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 shadow-none">
            <CardHeader className="text-[15px] font-semibold text-[#283252] dark:text-darkWhite-300">Hızlı Onboarding</CardHeader>
            <CardBody>
              <ul className="space-y-2 text-sm dark:text-darkWhite-200">
                {["Proje oluştur", "İlk görevini ekle", "Takvime etkinlik gir", "Bir arkadaşını davet et", "Profilini tamamla"].map((x, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {x}
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex gap-2">
                <Link to="/projects/create" className="w-full">
                  <Button radius="sm" className="w-full bg-orange-500 text-white shadow-none">Hemen Başla</Button>
                </Link>
                <Link to="/projects" className="w-full">
                  <Button radius="sm" className="w-full border border-gray-300 bg-white dark:bg-dark-300 text-[#283252] dark:text-darkWhite-300 shadow-none">Projeleri Gör</Button>
                </Link>
              </div>
            </CardBody>
          </Card>
          <Card className="border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 shadow-none">
            <CardHeader className="text-[15px] font-semibold text-[#283252] dark:text-darkWhite-300">Klavye Kısayolları</CardHeader>
            <CardBody>
              <div className="grid grid-cols-2 gap-y-2 text-sm dark:text-darkWhite-200">
                {[
                  { k: 'N', d: 'Yeni görev' },
                  { k: 'P', d: 'Yeni proje' },
                  { k: 'C', d: 'Takvim görünümü' },
                  { k: '/', d: 'Ara' },
                ].map((x, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="px-2 py-[2px] rounded-md border border-gray-300 bg-stone-50 text-[#283252] dark:text-darkWhite-300 text-xs">{x.k}</span>
                    <span>{x.d}</span>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </div>

        {/* FAQ */}
        <div className="mb-10">
          <h2 className="text-xl md:text-2xl font-semibold text-[#283252] dark:text-darkWhite-300 mb-6 text-center">
            Sık Sorulan Sorular
          </h2>
          <div className="space-y-3">
            {[
              { q: 'Proje nasıl oluşturulur?', a: 'Projeler sayfasında "Oluştur" ile başlayın; ad, açıklama ve ekip ayarlarınızı girin.' },
              { q: 'Takvime nasıl etkinlik eklerim?', a: 'Takvim sayfasında ilgili güne tıklayın ve etkinlik detaylarını doldurun.' },
              { q: 'Ekibimi nasıl davet ederim?', a: 'Organizasyonlar sayfasından davet linki oluşturabilir veya e-posta ile davet gönderebilirsiniz.' },
            ].map((item, i) => (
              <details key={i} className="group rounded-lg border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 p-4 open:border-orange-300">
                <summary className="cursor-pointer list-none flex items-center justify-between">
                  <span className="text-sm font-medium text-[#283252] dark:text-darkWhite-300">{item.q}</span>
                  <FiZap className="text-amber-500 opacity-80" />
                </summary>
                <div className="mt-2 text-sm opacity-80 dark:text-darkWhite-200">{item.a}</div>
              </details>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="rounded-xl border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 p-6 md:p-8 text-center shadow-none">
          <h3 className="text-lg md:text-xl font-semibold text-[#283252] dark:text-darkWhite-300 mb-2">
            Sorularınız mı var?
          </h3>
          <p className="text-sm opacity-80 dark:text-darkWhite-200 mb-4">
            Daha fazla yardım için destek ekibimizle iletişime geçebilirsiniz.
          </p>
          <div className="flex flex-col md:flex-row gap-3 justify-center items-center">
            <Link to="/premium">
              <Button radius="sm" className="border border-orange-300 bg-white dark:bg-dark-300 text-orange-700 shadow-none">Premium Özellikleri Keşfet</Button>
            </Link>
            <div className="text-xs opacity-60 dark:text-darkWhite-200 flex items-center gap-2">
              <FiCompass className="text-amber-500" />
              <span>İpucu: Dashboard'dan hızlıca erişim sağlayabilirsiniz</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NasilKullanirim;

